// src/pages/ambulance/ManageRequests.js
import React, { useState, useEffect } from 'react';
import {
  Ambulance,
  MapPin,
  Clock,
  User,
  Phone,
  CheckCircle,
  Filter,
  Search,
  Navigation
} from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const ManageRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await apiService.ambulances.getRequests(params);
      
      if (response.success) {
        setRequests(response.data.requests || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableResources = async () => {
    try {
      const [vehiclesRes, driversRes] = await Promise.all([
        apiService.ambulances.getVehicles(),
        apiService.ambulances.getDrivers()
      ]);

      if (vehiclesRes.success) {
        const available = vehiclesRes.data.vehicles.filter(v => v.status === 'available');
        setVehicles(available);
      }

      if (driversRes.success) {
        const available = driversRes.data.drivers.filter(d => d.status === 'available');
        setDrivers(available);
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  };

  const handleAcceptClick = async (request) => {
    setSelectedRequest(request);
    await fetchAvailableResources();
    setShowAcceptModal(true);
  };

  const handleAcceptRequest = async () => {
    if (!selectedVehicle || !selectedDriver) {
      alert('Please select both a vehicle and a driver');
      return;
    }

    try {
      setProcessing(true);
      const response = await apiService.ambulances.acceptRequest(
        selectedRequest._id,
        {
          vehicleId: selectedVehicle,
          driverId: selectedDriver
        }
      );

      if (response.success) {
        alert('Request accepted successfully!');
        setShowAcceptModal(false);
        fetchRequests();
      }
    } catch (err) {
      alert(err.message || 'Failed to accept request');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const response = await apiService.ambulances.updateRequestStatus(requestId, {
        status: newStatus
      });

      if (response.success) {
        alert('Status updated successfully!');
        fetchRequests();
      }
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      accepted: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Accepted' },
      en_route_pickup: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'En Route to Pickup' },
      arrived_pickup: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Arrived at Pickup' },
      patient_loaded: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Patient Loaded' },
      en_route_dropoff: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'En Route to Dropoff' },
      arrived_dropoff: { bg: 'bg-green-100', text: 'text-green-700', label: 'Arrived at Dropoff' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' }
    };

    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'CRITICAL' },
      high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HIGH' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'MEDIUM' },
      low: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'LOW' }
    };

    const badge = badges[priority] || badges.medium;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const filteredRequests = requests.filter(req =>
    req.patientInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.patientInfo?.condition?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.pickupLocation?.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Ambulance className="w-8 h-8 text-red-600" />
            Service Requests
          </h1>
          <p className="text-gray-600 mt-1">Manage ambulance service requests</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 w-5 h-5" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="en_route_pickup">En Route to Pickup</option>
              <option value="patient_loaded">Patient Loaded</option>
              <option value="en_route_dropoff">En Route to Dropoff</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Ambulance className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Requests Found</h3>
            <p className="text-gray-600">There are no requests matching your criteria.</p>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <div key={request._id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getPriorityBadge(request.priority)}
                  {getStatusBadge(request.status)}
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(request.requestedAt).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Patient Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-red-600" />
                    Patient Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-600">Name:</span> <span className="font-medium">{request.patientInfo?.name}</span></p>
                    <p><span className="text-gray-600">Age:</span> {request.patientInfo?.age || 'N/A'}</p>
                    <p><span className="text-gray-600">Condition:</span> <span className="font-medium text-red-600">{request.patientInfo?.condition}</span></p>
                    {request.patientInfo?.symptoms && (
                      <p><span className="text-gray-600">Symptoms:</span> {request.patientInfo.symptoms}</p>
                    )}
                    <p className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {request.patientInfo?.phone}
                    </p>
                  </div>
                </div>

                {/* Location Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    Location Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Pickup:</p>
                      <p className="font-medium">{request.pickupLocation?.address}</p>
                      {request.pickupLocation?.landmark && (
                        <p className="text-gray-500 text-xs mt-1">Landmark: {request.pickupLocation.landmark}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Dropoff:</p>
                      <p className="font-medium">{request.dropoffLocation?.address}</p>
                      {request.dropoffLocation?.facilityName && (
                        <p className="text-gray-500 text-xs mt-1">Facility: {request.dropoffLocation.facilityName}</p>
                      )}
                    </div>
                    {request.estimatedDistance && (
                      <p className="text-gray-600">
                        Distance: <span className="font-medium">{request.estimatedDistance.toFixed(1)} km</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {request.pricing && (
                    <span className="font-semibold text-gray-900">
                      Total: KES {request.pricing.totalAmount?.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {request.status === 'pending' && (
                    <button
                      onClick={() => handleAcceptClick(request)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Accept Request
                    </button>
                  )}
                  {request.status === 'accepted' && (
                    <button
                      onClick={() => handleUpdateStatus(request._id, 'en_route_pickup')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Navigation className="w-4 h-4" />
                      En Route to Pickup
                    </button>
                  )}
                  {request.status === 'en_route_pickup' && (
                    <button
                      onClick={() => handleUpdateStatus(request._id, 'arrived_pickup')}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Arrived at Pickup
                    </button>
                  )}
                  {request.status === 'arrived_pickup' && (
                    <button
                      onClick={() => handleUpdateStatus(request._id, 'patient_loaded')}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                    >
                      Patient Loaded
                    </button>
                  )}
                  {request.status === 'patient_loaded' && (
                    <button
                      onClick={() => handleUpdateStatus(request._id, 'en_route_dropoff')}
                      className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                    >
                      En Route to Dropoff
                    </button>
                  )}
                  {request.status === 'en_route_dropoff' && (
                    <button
                      onClick={() => handleUpdateStatus(request._id, 'arrived_dropoff')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Arrived at Dropoff
                    </button>
                  )}
                  {request.status === 'arrived_dropoff' && (
                    <button
                      onClick={() => handleUpdateStatus(request._id, 'completed')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Complete Trip
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Accept Request Modal */}
      {showAcceptModal && (
        <Modal
          isOpen={showAcceptModal}
          onClose={() => setShowAcceptModal(false)}
          title="Accept Service Request"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Vehicle *
              </label>
              <select
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              >
                <option value="">Choose a vehicle...</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle._id} value={vehicle._id}>
                    {vehicle.registrationNumber} - {vehicle.vehicleType} ({vehicle.ambulanceClass})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Driver *
              </label>
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              >
                <option value="">Choose a driver...</option>
                {drivers.map((driver) => (
                  <option key={driver._id} value={driver._id}>
                    {driver.firstName} {driver.lastName} - {driver.licenseNumber}
                  </option>
                ))}
              </select>
            </div>

            {vehicles.length === 0 && drivers.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-700">
                  No available vehicles or drivers. Please add them first.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleAcceptRequest}
                disabled={processing || !selectedVehicle || !selectedDriver}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Accepting...' : 'Accept Request'}
              </button>
              <button
                onClick={() => setShowAcceptModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ManageRequests;